import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";

import { FoodMenu } from "@/components/site/FoodMenu";
import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { pathFor } from "@/lib/i18n";

const TITLE = "Menu and School Meals – Swedish International School";
const DESCRIPTION =
  "How school meals work at our school in Estepona and Marbella: a cooked lunch every school day, special diets on request and a menu published when ready.";

export const Route = createFileRoute("/en/menu")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/menu") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/menu"),
  }),
  component: MenuPage,
});

function MenuPage() {
  return (
    <>
      <PageHero
        eyebrow="Practical info"
        title="Menu"
        intro="Lunch is served at school on regular school days. The menu is published here on this page – no menu is published during school holidays."
      />

      <FoodMenu locale="en" />

      <Prose>
        <h2>Lunch every school day</h2>
        <p>
          Lunch is served at school on regular school days. Meals alternate between
          Swedish and Spanish dishes, with vegetables, salad and vegetarian options as a recurring
          feature.
        </p>


        <h2>Special diets and allergies</h2>
        <p>
          We take allergies, intolerances and dietary requirements for religious or ethical reasons
          into account. State the requirement in the{" "}
          <Link className="text-primary link-quiet" to={pathFor("/ansokan", "en") as never}>
            application
          </Link>{" "}
          or contact the school, and we'll go through it together with the kitchen before school
          starts.
        </p>

        <h2>Meals during the day</h2>
        <p>
          Lunch is the main meal of the day and is eaten together at an unhurried pace — it is also an
          opportunity to practise Swedish in everyday conversation. The youngest children eat with
          staff at the table. If your child needs fruit or a snack in addition to lunch, we agree on
          that in advance.
        </p>

        <h2>Who prepares the food</h2>
        <p>
          Meals are cooked and delivered by a kitchen the school works with. The menu is published
          here as soon as it is ready – no menu is published during school holidays. Dishes vary
          from week to week and include both Swedish home cooking and local Spanish elements.
        </p>

        <h2>Questions about the food</h2>
        <p>
          If you have any questions about meals, timings or special diets, please feel free to
          email{" "}
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">
            info@sweintschool.com
          </a>
          .
        </p>
      </Prose>

      <div className="pb-16 sm:pb-24">
        <CtaBand locale="en" />
      </div>
    </>
  );
}
